// Single registry of gateable feature keys, shared by backend, web and mobile
// exactly like `shared/permissions.ts`. Entitlements and permissions answer
// different questions: *entitlement* = has the company bought it (this file);
// *permission* = may this user use it (`shared/permissions.ts`). A surface
// shows only when both pass. See docs/plans/TIER_GATING_PLAN.md sections 3.1/3.2.
//
// Baseline (Essentials-tier) modules never appear here — they are ungated by
// design and every plan gets them for free.

export const FEATURES = {
  purchasing: "purchasing",
  vendors: "vendors",
  vendor_bills: "vendor_bills",
  progress_billing: "progress_billing",
  agreements: "agreements",
  bundles: "bundles",
  doc_numbering: "doc_numbering",
  multi_tax: "multi_tax",
  line_media: "line_media",
  crew_scheduling: "crew_scheduling",
  dispatch_popouts: "dispatch_popouts",
  route_optimization: "route_optimization",
  tech_color_calendar: "tech_color_calendar",
  acct_xero: "acct_xero",
  acct_qbo: "acct_qbo",
  review_requests: "review_requests",
  team_chat: "team_chat",
  custom_fields: "custom_fields",
  required_fields: "required_fields",
  reports_full: "reports_full",
  photo_quote: "photo_quote",
  activity_history: "activity_history",
  advanced_permissions: "advanced_permissions",
  sales_pipeline: "sales_pipeline",
  equipment_tracking: "equipment_tracking",
  site_equipment: "site_equipment",
  report_writer: "report_writer",
  config_audit_reports: "config_audit_reports",
  security_matrix_report: "security_matrix_report",
  addon_siteview360: "addon_siteview360",
  addon_fleet: "addon_fleet",
  addon_api: "addon_api",
} as const;

export type FeatureKey = typeof FEATURES[keyof typeof FEATURES];

export const ALL_FEATURE_KEYS: FeatureKey[] = Object.values(FEATURES);

/**
 * minRank: the lowest `subscription_tier.tier_rank` that includes this feature
 * by default — 2 = Pro, 3 = Elite, null = add-on only (bought independently of
 * tier, at any tier). `addon_api` also carries minRank 3 because Elite
 * includes API access without needing to buy the add-on separately.
 */
export const FEATURE_META: Record<FeatureKey, { label: string; minRank: 2 | 3 | null; addonKey?: string }> = {
  purchasing: { label: "Purchase Orders", minRank: 2 },
  vendors: { label: "Vendors", minRank: 2 },
  vendor_bills: { label: "Vendor Bills", minRank: 2 },
  progress_billing: { label: "Progress Billing", minRank: 2 },
  agreements: { label: "Service Agreements", minRank: 2 },
  bundles: { label: "Bundles", minRank: 2 },
  doc_numbering: { label: "Custom Document Numbering", minRank: 2 },
  multi_tax: { label: "Multi-Rate Tax", minRank: 2 },
  line_media: { label: "Line Item Media", minRank: 2 },
  crew_scheduling: { label: "Crew Scheduling", minRank: 2 },
  dispatch_popouts: { label: "Dispatch Popouts", minRank: 2 },
  route_optimization: { label: "Route Optimization", minRank: 2 },
  tech_color_calendar: { label: "Technician Color Calendar", minRank: 2 },
  acct_xero: { label: "Xero Accounting Sync", minRank: 2 },
  acct_qbo: { label: "QuickBooks Online Sync", minRank: 2 },
  review_requests: { label: "Review Requests", minRank: 2 },
  team_chat: { label: "Team Chat", minRank: 2 },
  custom_fields: { label: "Custom Fields", minRank: 2 },
  required_fields: { label: "Required Fields", minRank: 2 },
  reports_full: { label: "Full Reports", minRank: 2 },
  photo_quote: { label: "Photo Quote", minRank: 2 },
  activity_history: { label: "Activity History", minRank: 2 },
  advanced_permissions: { label: "Advanced Permissions", minRank: 2 },
  sales_pipeline: { label: "Sales Pipeline", minRank: 3 },
  equipment_tracking: { label: "Equipment Tracking", minRank: 3 },
  site_equipment: { label: "Site Equipment", minRank: 3 },
  report_writer: { label: "Report Writer", minRank: 3 },
  config_audit_reports: { label: "Configuration Audit Reports", minRank: 3 },
  security_matrix_report: { label: "Security Matrix Report", minRank: 3 },
  addon_siteview360: { label: "SiteView360", minRank: null, addonKey: "siteview360" },
  addon_fleet: { label: "Fleet", minRank: null, addonKey: "fleet" },
  addon_api: { label: "API Access", minRank: 3, addonKey: "api_access" },
};

export type PlanStatus = "trial" | "active" | "past_due" | "expired" | "grandfathered";

export type Entitlements = {
  planId: string | null;
  planName: string;
  planKind: "solo" | "team" | "internal";
  tierRank: number | null;
  status: PlanStatus;
  seats: {
    included: number;
    extra: number;
    limit: number | null;
    used: number;
  };
  features: FeatureKey[];
  /** Grandfathered / internal tiers get every feature regardless of `features`. */
  allFeatures: boolean;
  /**
   * Gap 1 (docs/plans/CORP_RECURRING_BILLING_PLAN.md §7 ruling D5): true only when Corp
   * `subscription.status` is EXACTLY `"blocked"` (past its dunning grace deadline with no
   * payment). Optional — omitted/undefined must be treated as `false` (fail-open), same
   * contract as the rest of this type when entitlements fail to load. Drives the read-only
   * banner (web `BillingBlockedBanner`) and the neutral mobile message; actual write
   * enforcement happens server-side in `backend/src/middleware/billing-block.ts`, this field
   * is display-only.
   */
  billingBlocked?: boolean;
  /**
   * Owner ruling (Tony, 2026-10-02): richer billing-state surface for the web banner (and any
   * future mobile equivalent) — the raw Corp `subscription.status`, its grace deadline, a
   * computed `readOnly` (mirrors `billingBlocked` above — kept as a separate field so a reader
   * doesn't have to know that `billingBlocked` is the same thing under a different name), and a
   * `reason` for WHY it's read-only. Optional — omitted/undefined must be treated as
   * `{ status: null, graceDeadline: null, readOnly: false, reason: null }` (fail-open, same
   * contract as `billingBlocked`). Never carries any Corp id (subscriber id, etc.) — tenant
   * clients never see Corp primary keys.
   */
  billing?: {
    status: string | null;
    graceDeadline: string | null;
    readOnly: boolean;
    reason: "blocked" | "trial_ended_no_card" | "cancelled" | null;
    /**
     * G-27 offboarding (docs/plans/tax-gaps-2026-10-11/G27_OFFBOARDING_SPEC.md). Optional for
     * backward compatibility; omitted must be read as false/null. `accountCancelled` = Corp
     * subscription status is "cancelled": the account is READ-ONLY (writes get 402
     * `ACCOUNT_CANCELLED`); `readOnly` is also true and `reason` is "cancelled".
     * `cancelAtPeriodEnd` = cancellation requested, still writable until `periodEndsAt`.
     * `purgeAfter` = date the held data is permanently deleted. `lastFullExportAt` = last COMPLETE
     * financial export recorded in Corp. All values lag Corp by up to 5 minutes (license cache).
     */
    accountCancelled?: boolean;
    cancelAtPeriodEnd?: boolean;
    /** Corp subscription.cancelled_at (when the account was cancelled); null/omitted when unknown. */
    cancelledAt?: string | null;
    periodEndsAt?: string | null;
    purgeAfter?: string | null;
    lastFullExportAt?: string | null;
  };
};

/**
 * FAIL-OPEN when entitlements are missing (null/undefined) — mirrors the
 * mobile permission-safety rule (see `mobile` gotchas: mobile permission
 * checks fail open, not closed, when data hasn't loaded yet) so a slow or
 * failed `/api/entitlements` fetch never locks a paying tenant out of their
 * own data. The API-layer `feature-guard.ts` (Phase 1B/2) is the only layer
 * that counts for security and must resolve entitlements itself rather than
 * trusting a client-supplied value.
 */
export function hasFeature(ent: Entitlements | null | undefined, key: FeatureKey): boolean {
  if (!ent) return true;
  if (ent.allFeatures) return true;
  return ent.features.includes(key);
}

export function minimumPlanFor(key: FeatureKey): "Pro" | "Elite" | "Add-on" {
  const meta = FEATURE_META[key];
  if (!meta || meta.minRank === null) return "Add-on";
  return meta.minRank === 3 ? "Elite" : "Pro";
}
